import { useEffect, useMemo, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { languageLabel, languageLabelWithCode } from '@/shared/lib/language'
import { explain } from '@/shared/api/problem'
import type { SurveyInstrumentDto } from '@/shared/api/types'
import { setConsentScript } from './api'
import { Pill } from './vocab'

/**
 * What the interviewer reads out before anything else happens.
 *
 * ## Nothing else in the application can create one
 *
 * `PUT /surveys/{id}/consent-script` is the only route, and `→ LIVE` is refused
 * without a script when the poll requires consent. Until this dialog existed the
 * refusal was unanswerable from inside the product: a poll could be built,
 * staffed and scheduled, and then simply not start, with the blocker naming a
 * thing no screen could enter.
 *
 * ## The audio disclosure is a required field with a reason attached
 *
 * When `audioRecordingMode` is not `OFF`, the server refuses a script with no
 * disclosure — consent to be interviewed is not consent to be recorded, and the
 * two are separate under the Data Protection Act the same way they are separate
 * in the schema. The dialog marks it required and prints the poll's own
 * recording mode next to it, so the requirement arrives before the save rather
 * than as a 400 nobody predicted.
 *
 * ## The script is per POLL, not per version
 *
 * So it is not frozen by publication and there is no "create version N+1" path
 * here: correcting the wording of a consent script does not change a question
 * anybody answered. That is why this dialog is the one instrument-adjacent write
 * that still works on a published version.
 */
export function ConsentScriptDialog({ open, instrument, onClose, onSaved }: {
  open: boolean
  /** The document. Languages, existing scripts and the recording mode all come from it. */
  instrument: SurveyInstrumentDto
  onClose: () => void
  /** 204 — nothing comes back, so the page re-reads the instrument. */
  onSaved: () => void
}) {
  const scripts = instrument.consentScripts
  const audioOn = instrument.audioRecordingMode !== 'OFF'

  /** Every language this poll might be read in: declared on the instrument, or already scripted. */
  const knownLanguages = useMemo(() => {
    const seen = new Set<string>([...instrument.languages, ...scripts.map((s) => s.languageCode)])
    return [...seen].sort()
  }, [instrument.languages, scripts])

  const missing = useMemo(
    () => instrument.languages.filter((l) => !scripts.some((s) => s.languageCode === l)),
    [instrument.languages, scripts])

  const [languageCode, setLanguageCode] = useState('')
  const [newLanguage, setNewLanguage] = useState('')
  const [body, setBody] = useState('')
  const [audioDisclosure, setAudioDisclosure] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  /** Open on the first gap, because that is the one blocking `→ LIVE`. */
  useEffect(() => {
    if (!open) return
    const first = missing[0] ?? knownLanguages[0] ?? ''
    setLanguageCode(first)
    setNewLanguage('')
    setError(null)
    setSaved(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const effectiveLanguage = languageCode === '__new' ? newLanguage.trim() : languageCode
  const existing = scripts.find((s) => s.languageCode === effectiveLanguage) ?? null

  /**
   * Load the script already on file whenever the language changes.
   *
   * Without this, opening the dialog on Kiswahili and typing over an English
   * body would overwrite the Kiswahili script with English wording — the PUT is
   * an upsert keyed on the language, so there is no version of this that fails
   * loudly.
   *
   * Only for a language that was CHOSEN, never for one being typed. While the
   * selection is "another language…" the code changes on every keystroke, and
   * re-seeding on each of them empties the textarea under an author halfway
   * through an eight-thousand-character script — including when they are
   * correcting a typo in the code. Nothing recovers what that wipes.
   */
  useEffect(() => {
    if (!open || languageCode === '__new') return
    setBody(existing?.body ?? '')
    setAudioDisclosure(existing?.audioDisclosure ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, languageCode, effectiveLanguage])

  async function submit() {
    setBusy(true)
    setError(null)
    setSaved(null)
    try {
      await setConsentScript(instrument.surveyId, {
        languageCode: effectiveLanguage,
        body: body.trim(),
        audioDisclosure: audioDisclosure.trim() || undefined,
      })
      setSaved(effectiveLanguage)
      onSaved()
    } catch (e) {
      // The audio-disclosure 400 lands here with the poll's ref and its recording
      // mode in the sentence. So does an empty body, which the server refuses
      // because a consent script with no words is not consent.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title="Consent script"
      description="Read out before the first question. A poll that requires consent cannot go live without one in every language it is fielded in."
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="erp-btn erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Close
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : existing ? `Save ${effectiveLanguage} script` : 'Add script'}
          </button>
        </div>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />
        {saved && (
          <Note tone="good" onDismiss={() => setSaved(null)}>
            The {saved} script is saved. It is stored against the poll, so every version of the
            instrument is introduced with the same words.
          </Note>
        )}

        <Coverage languages={instrument.languages} scripts={scripts}
          consentRequired={instrument.consentRequired} />

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-5">
            <Field label="Language" required
              hint="A script is per language, because it decides who it can be read to.">
              <select className="erp-input h-9 w-full" value={languageCode}
                onChange={(e) => setLanguageCode(e.target.value)}>
                {knownLanguages.length === 0 && <option value="">Choose…</option>}
                {knownLanguages.map((l) => (
                  <option key={l} value={l}>
                    {languageLabelWithCode(l)}
                    {scripts.some((s) => s.languageCode === l) ? ' — has a script' : ' — missing'}
                  </option>
                ))}
                <option value="__new">Another language…</option>
              </select>
            </Field>
          </div>
          {languageCode === '__new' && (
            <div className="col-span-3">
              <Field label="Code" required hint="At most 8 characters, e.g. sw.">
                <input className="erp-input h-9 w-full font-mono text-erp-sm" maxLength={8}
                  value={newLanguage} placeholder="sw"
                  onChange={(e) => setNewLanguage(e.target.value)} />
              </Field>
            </div>
          )}
          <div className="col-span-4 self-center pt-3">
            <p className="text-erp-xs text-muted-foreground">
              {existing
                ? 'A script already exists for this language. Saving replaces its wording.'
                : 'No script for this language yet.'}
            </p>
          </div>
        </div>

        <Field label="Script" required
          hint="Who is asking, on whose behalf, what for, how long it takes, and that taking part is voluntary.">
          <textarea className="erp-input w-full min-h-[140px] py-2" value={body} maxLength={8000}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Good morning. My name is … and I am calling on behalf of …" />
        </Field>

        <Field label="Audio disclosure" required={audioOn}
          hint={audioOn
            ? `This poll records audio (${instrument.audioRecordingMode}), and the server refuses a script that does not say so. Consent to be interviewed is not consent to be recorded.`
            : 'This poll does not record audio, so a disclosure is optional. Add one only if that may change — an unused clause is better than a recording nobody was told about.'}>
          <textarea className="erp-input w-full min-h-[72px] py-2" value={audioDisclosure}
            maxLength={4000} onChange={(e) => setAudioDisclosure(e.target.value)}
            placeholder="This conversation will be recorded so a supervisor can verify it." />
        </Field>

        {audioOn && audioDisclosure.trim() === '' && (
          <Note tone="bad">
            {instrument.surveyRef} records audio ({instrument.audioRecordingMode}) and this script
            has no disclosure. The server refuses the save, and it refuses `→ LIVE` for the same
            reason — this is the same requirement arriving early enough to answer.
          </Note>
        )}
      </div>
    </Modal>
  )
}

/**
 * Which languages have a script and which do not.
 *
 * Rendered as two lists rather than a count, because "3 of 4" does not say which
 * one is missing and the missing one is the whole message.
 */
function Coverage({ languages, scripts, consentRequired }: {
  languages: string[]
  scripts: SurveyInstrumentDto['consentScripts']
  consentRequired: boolean
}) {
  const scripted = scripts.map((s) => s.languageCode)
  const missing = languages.filter((l) => !scripted.includes(l))
  /** A script in a language the instrument is not fielded in is not a gap, but it is worth seeing. */
  const extra = scripted.filter((l) => !languages.includes(l))

  return (
    <div className="rounded border border-border bg-muted/20 p-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5 text-erp-sm">
        <span className="text-muted-foreground">Has a script:</span>
        {scripted.length === 0
          ? <span className="text-muted-foreground">none yet</span>
          : scripted.map((l) => <Pill key={l}>{languageLabel(l)}</Pill>)}
      </div>
      {missing.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-erp-sm">
          <span className="text-muted-foreground">Missing:</span>
          {missing.map((l) => <Pill key={l}>{languageLabel(l)}</Pill>)}
        </div>
      )}
      {extra.length > 0 && (
        <p className="text-erp-xs text-muted-foreground">
          {extra.map(languageLabel).join(', ')} {extra.length === 1 ? 'has' : 'have'} a script but the instrument is
          not translated into {extra.length === 1 ? 'it' : 'them'} — an introduction in a language
          the questions are not written in.
        </p>
      )}
      <p className="text-erp-xs text-muted-foreground">
        {consentRequired
          ? 'This poll requires consent, so `→ LIVE` is refused until every language it is fielded in has one.'
          : 'This poll does not require recorded consent, so no script blocks `→ LIVE`. One is still what the interviewer reads out.'}
      </p>
    </div>
  )
}
